import { type JSX, Show, untrack } from 'solid-js';
import { Dynamic } from 'solid-js/web';

// THE form field. Every labelled control this app paints sits in this scaffold,
// so the id scheme and the a11y join live in ONE place:
// `styles/field-usage.test.ts` refuses the `field` / `field__label` classes
// anywhere else.
//
// What it guarantees, for every call site at once:
// - the control is NAMED by the label (`label[for=id]`), so `getByLabel` in e2e
//   and `getByLabelText` in unit tests find it;
// - the hint and the error have derived ids (`${id}-desc`, `${id}-error`) and
//   the control's `aria-describedby` points at whichever of them is showing;
// - `aria-invalid` is set exactly while an error is showing, and the error line
//   is `role="alert"` so it is announced when it appears.
//
// The control is a render prop: Field cannot know whether it is an `<input>`, a
// `<select>` or a `<textarea>`, so it hands the attributes over and the caller
// spreads them — `{(a11y) => <input {...a11y} class="input" />}`. The object's
// members are getters, so a spread stays live as `desc`/`error` change; do not
// destructure it. It is called once, untracked, like any Solid component body.
//
// The rendered `<label>`/`<legend>` also carries `id={id}-label` (NOT exposed
// on `FieldA11y` — a getter there would spread onto every native control this
// app has via `{...a11y}`, setting a stray, meaningless `labelid` DOM
// attribute). `Select.tsx` (epic #5620 slice 02) is this id's one consumer: its
// open listbox needs an accessible name, and `aria-labelledby` pointing at the
// TRIGGER does not work when the trigger's own visible content is empty (an
// unset value) — per the accname algorithm, a node reached via
// `aria-labelledby` does not re-apply ITS OWN host-language `<label for>`
// association, only its own content/value, so an empty trigger resolves to no
// name regardless of the label pointing at it (confirmed against axe-core
// 2026-10, `Select.test.tsx`'s "is axe-clean, open" case). Pointing at the
// label element's OWN id sidesteps that recursion entirely: the label's text
// is read as ordinary content, label[for] association never enters it.
//
// `as="fieldset"` is for a GROUP of controls (a checkbox list, a radio set): the
// label renders as the `<legend>`, which names the group, and `aria-describedby`
// rides on the `<fieldset>` itself — the group is what the hint describes. The
// render prop then receives only `id`, a prefix for the controls' own ids.
//
// Class contract (styled in `styles/ui/_forms.scss`): `.field` > `.field__label`,
// control, `p.field__desc`, `p.field__error`. A standalone `field__desc` /
// `field__error` (a form-level mutation error, a section hint) is not a field and
// stays where it is.

export type FieldA11y = {
  readonly id: string;
  // `| undefined`: the members are getters, absent is `undefined` on read.
  readonly 'aria-describedby'?: string | undefined;
  readonly 'aria-invalid'?: true | undefined;
};

export type FieldProps = {
  /** Already translated. Names the control (or, as a fieldset, the group). */
  readonly label: string;
  /** The control's id; also the prefix of the hint and error ids. */
  readonly id: string;
  /** Already translated hint under the control; `undefined` shows none (so a
   * conditional hint is `desc={cond() ? t('…') : undefined}`). */
  readonly desc?: string | undefined;
  /** Already translated; showing iff non-empty. */
  readonly error?: string | null | undefined;
  /** Already translated; the label's `title`, a longer explanation kept off the page
   * until asked for (hover, or a long-press) so the field stays one line tall. It is
   * additive, never the only copy of something a user must read to use the control. */
  readonly labelTitle?: string | undefined;
  readonly as?: 'div' | 'fieldset';
  readonly children: (a11y: FieldA11y) => JSX.Element;
};

export function Field(props: FieldProps): JSX.Element {
  const isGroup = (): boolean => props.as === 'fieldset';
  const hasDesc = (): boolean => props.desc !== undefined && props.desc !== '';
  const hasError = (): boolean =>
    props.error !== undefined && props.error !== null && props.error !== '';
  const describedBy = (): string | undefined => {
    const ids = [
      hasDesc() ? `${props.id}-desc` : undefined,
      hasError() ? `${props.id}-error` : undefined,
    ].filter((v): v is string => v !== undefined);
    return ids.length === 0 ? undefined : ids.join(' ');
  };

  const a11y: FieldA11y = {
    get id() {
      return props.id;
    },
    get 'aria-describedby'() {
      return isGroup() ? undefined : describedBy();
    },
    get 'aria-invalid'() {
      return !isGroup() && hasError() ? true : undefined;
    },
  };
  const control = untrack(() => props.children(a11y));

  return (
    <Dynamic
      component={isGroup() ? 'fieldset' : 'div'}
      class="field"
      aria-describedby={isGroup() ? describedBy() : undefined}
    >
      <Show
        when={isGroup()}
        fallback={
          <label
            class="field__label"
            id={`${props.id}-label`}
            for={props.id}
            title={props.labelTitle}
          >
            {props.label}
          </label>
        }
      >
        <legend class="field__label" id={`${props.id}-label`} title={props.labelTitle}>
          {props.label}
        </legend>
      </Show>
      {control}
      <Show when={hasDesc()}>
        <p id={`${props.id}-desc`} class="field__desc">
          {props.desc}
        </p>
      </Show>
      <Show when={hasError()}>
        <p id={`${props.id}-error`} class="field__error" role="alert">
          {props.error}
        </p>
      </Show>
    </Dynamic>
  );
}
